import React, { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  format, isSameMonth, isSameDay, addMonths, subMonths, parseISO,
} from "date-fns";
import { ChevronLeft, ChevronRight, Plus, CheckSquare, FileText, Handshake, CalendarClock, Trash2, Loader2, Plane, CalendarPlus, Copy, Check, Download, RefreshCw, Unplug, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, PageLoader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const TYPE_META = {
  event: { label: "Event", color: "bg-accent", text: "text-accent", icon: CalendarClock },
  task: { label: "Task", color: "bg-blue-500", text: "text-blue-500", icon: CheckSquare },
  invoice: { label: "Invoice due", color: "bg-amber-500", text: "text-amber-500", icon: FileText },
  deal: { label: "Opportunity", color: "bg-violet-500", text: "text-violet-500", icon: Handshake },
  trip: { label: "Trip", color: "bg-emerald-500", text: "text-emerald-500", icon: Plane },
  google: { label: "Google Calendar", color: "bg-sky-400", text: "text-sky-400", icon: ExternalLink },
};

export default function Calendar() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(new Date());
  const [dayOpen, setDayOpen] = useState(null); // Date
  const [eventOpen, setEventOpen] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: gstatus } = useQuery({ queryKey: ["gcal-status"], queryFn: async () => (await api.get("/calendar/google/status")).data });

  React.useEffect(() => {
    const g = searchParams.get("google");
    if (!g) return;
    if (g === "connected") { toast.success("Google Calendar connected — syncing now"); qc.invalidateQueries({ queryKey: ["gcal-status"] }); setTimeout(() => qc.invalidateQueries({ queryKey: ["calendar"] }), 4000); }
    else toast.error(g === "denied" ? "Google Calendar access was declined." : "Couldn't connect Google Calendar. Please try again.");
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, qc]);

  // Keep Google in sync while the calendar is open (at most every 5 minutes)
  React.useEffect(() => {
    if (!gstatus?.connected) return;
    const stale = !gstatus.last_sync || Date.now() - new Date(gstatus.last_sync).getTime() > 5 * 60 * 1000;
    if (stale) api.post("/calendar/google/sync").then(() => { qc.invalidateQueries({ queryKey: ["calendar"] }); qc.invalidateQueries({ queryKey: ["gcal-status"] }); }).catch(() => {});
  }, [gstatus?.connected, gstatus?.last_sync, qc]);
  const [form, setForm] = useState({ title: "", date: "", time: "", notes: "", contact_id: "", contact_name: "", deal_id: "", deal_title: "" });

  const { data, isLoading } = useQuery({ queryKey: ["calendar"], queryFn: async () => (await api.get("/calendar")).data });
  const { data: contacts = [] } = useQuery({ queryKey: ["contacts"], queryFn: async () => (await api.get("/contacts")).data });
  const { data: deals = [] } = useQuery({ queryKey: ["deals"], queryFn: async () => (await api.get("/deals")).data });
  const items = data?.items || [];

  const byDate = useMemo(() => {
    const map = {};
    for (const it of items) { (map[it.date] = map[it.date] || []).push(it); }
    return map;
  }, [items]);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 0 });
    return eachDayOfInterval({ start, end });
  }, [cursor]);

  const openAddEvent = (date) => {
    setForm({ title: "", date: date ? format(date, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"), time: "", notes: "", contact_id: "", contact_name: "", deal_id: "", deal_title: "" });
    setEventOpen(true);
  };

  const saveEvent = async () => {
    try {
      await api.post("/events", form);
      toast.success("Event added");
      setEventOpen(false);
      qc.invalidateQueries({ queryKey: ["calendar"] });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const deleteEvent = async (id) => {
    await api.delete(`/events/${id}`);
    toast.success("Event removed");
    qc.invalidateQueries({ queryKey: ["calendar"] });
  };

  const dayItems = dayOpen ? (byDate[format(dayOpen, "yyyy-MM-dd")] || []) : [];

  return (
    <div>
      <PageHeader
        title="Calendar"
        subtitle="Everything in one place — meetings, tasks, invoice due dates and opportunity closes."
        testId="calendar-header"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setSyncOpen(true)} data-testid="sync-calendar-button"><CalendarPlus className="mr-2 h-4 w-4" /> Sync calendar{gstatus?.connected && <span className="ml-2 h-2 w-2 rounded-full bg-accent" data-testid="gcal-connected-dot" />}</Button>
            <Button onClick={() => openAddEvent(new Date())} data-testid="add-event-button"><Plus className="mr-2 h-4 w-4" /> Add event</Button>
          </div>
        }
      />

      {isLoading ? <PageLoader /> : (
        <div className="rounded-2xl border border-border bg-card p-4 sm:p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-xl font-bold">{format(cursor, "MMMM yyyy")}</h2>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" onClick={() => setCursor(subMonths(cursor, 1))} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="cal-today">Today</Button>
              <Button variant="outline" size="icon" onClick={() => setCursor(addMonths(cursor, 1))} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-px overflow-hidden rounded-xl border border-border bg-border">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className="bg-secondary/60 py-2 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">{d}</div>
            ))}
            {days.map((day) => {
              const key = format(day, "yyyy-MM-dd");
              const dItems = byDate[key] || [];
              const inMonth = isSameMonth(day, cursor);
              const today = isSameDay(day, new Date());
              return (
                <button key={key} onClick={() => setDayOpen(day)} data-testid={`cal-day-${key}`}
                  className={`min-h-[92px] bg-card p-1.5 text-left align-top transition-colors hover:bg-secondary/40 ${inMonth ? "" : "opacity-40"}`}>
                  <div className={`mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${today ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{format(day, "d")}</div>
                  <div className="space-y-1">
                    {dItems.slice(0, 3).map((it) => {
                      const meta = TYPE_META[it.type];
                      return (
                        <div key={`${it.type}-${it.id}`} className="flex items-center gap-1 truncate rounded px-1 py-0.5 text-[11px] bg-secondary" title={it.title}>
                          <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${meta.color}`} />
                          <span className="truncate">{it.title}</span>
                        </div>
                      );
                    })}
                    {dItems.length > 3 && <p className="pl-1 text-[10px] text-muted-foreground">+{dItems.length - 3} more</p>}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-4 flex flex-wrap gap-4">
            {Object.entries(TYPE_META).map(([k, m]) => (
              <div key={k} className="flex items-center gap-1.5 text-xs text-muted-foreground"><span className={`h-2 w-2 rounded-full ${m.color}`} /> {m.label}</div>
            ))}
          </div>
        </div>
      )}

      {/* Day detail */}
      <Dialog open={!!dayOpen} onOpenChange={(o) => !o && setDayOpen(null)}>
        <DialogContent data-testid="cal-day-dialog">
          <DialogHeader><DialogTitle>{dayOpen && format(dayOpen, "EEEE, MMM d")}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {dayItems.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Nothing scheduled.</p>
            ) : dayItems.map((it) => {
              const meta = TYPE_META[it.type];
              const Icon = meta.icon;
              const isTrip = it.type === "trip";
              const isGoogle = it.type === "google";
              const clickable = isTrip || (isGoogle && it.html_link);
              const onOpen = isTrip ? () => navigate(`/travel?trip=${it.trip_id}`) : isGoogle ? () => window.open(it.html_link, "_blank", "noopener") : undefined;
              return (
                <div key={`${it.type}-${it.id}`} className={`flex items-start gap-3 rounded-xl border border-border p-3 ${clickable ? "cursor-pointer transition-colors hover:bg-secondary/60" : ""}`} onClick={onOpen} data-testid={`cal-item-${it.id}`}>
                  <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-secondary ${meta.text}`}><Icon className="h-4 w-4" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{it.title}{it.time ? ` · ${it.time}` : ""}</p>
                    {it.subtitle && <p className="text-xs text-muted-foreground">{it.subtitle}</p>}
                    <p className={`mt-0.5 text-[11px] font-semibold uppercase tracking-wider ${meta.text}`}>{meta.label}{isTrip ? " · tap to open" : isGoogle ? " · opens in Google" : ""}</p>
                  </div>
                  {it.type === "event" && (
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={(e) => { e.stopPropagation(); deleteEvent(it.id); }} data-testid={`delete-event-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter><Button onClick={() => { const d = dayOpen; setDayOpen(null); openAddEvent(d); }} data-testid="day-add-event-button"><Plus className="mr-2 h-4 w-4" /> Add event this day</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add event */}
      <Dialog open={eventOpen} onOpenChange={setEventOpen}>
        <DialogContent data-testid="event-dialog">
          <DialogHeader><DialogTitle>New event</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Discovery call with Sarah" data-testid="event-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="event-date-input" /></div>
              <div className="space-y-1.5"><Label>Time</Label><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="event-time-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Link contact</Label>
                <Select value={form.contact_id || "none"} onValueChange={(v) => { const c = contacts.find((x) => x.id === v); setForm({ ...form, contact_id: v === "none" ? "" : v, contact_name: c ? c.name : "" }); }}>
                  <SelectTrigger data-testid="event-contact-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Link opportunity</Label>
                <Select value={form.deal_id || "none"} onValueChange={(v) => { const d = deals.find((x) => x.id === v); setForm({ ...form, deal_id: v === "none" ? "" : v, deal_title: d ? d.title : "" }); }}>
                  <SelectTrigger data-testid="event-deal-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {deals.map((d) => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="event-notes-input" /></div>
          </div>
          <DialogFooter><Button onClick={saveEvent} disabled={!form.title || !form.date} data-testid="save-event-button">Add event</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <SyncDialog open={syncOpen} onOpenChange={setSyncOpen} gstatus={gstatus} />
    </div>
  );
}

function GoogleSyncSection({ gstatus }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState("");
  const connect = async () => {
    setBusy("connect");
    try { const r = await api.get("/calendar/google/connect"); window.location.href = r.data.url; }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setBusy(""); }
  };
  const syncNow = async () => {
    setBusy("sync");
    try {
      const r = await api.post("/calendar/google/sync");
      const { push, pull } = r.data;
      toast.success(`Synced — ${push.created + push.updated} sent to Google, ${pull.imported} pulled in`);
      qc.invalidateQueries({ queryKey: ["calendar"] }); qc.invalidateQueries({ queryKey: ["gcal-status"] });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar? Events BizForge added to Google will be removed; your own Google events are untouched.")) return;
    setBusy("disconnect");
    try { await api.delete("/calendar/google/disconnect"); toast.success("Google Calendar disconnected"); qc.invalidateQueries({ queryKey: ["calendar"] }); qc.invalidateQueries({ queryKey: ["gcal-status"] }); }
    catch { toast.error("Couldn't disconnect."); }
    finally { setBusy(""); }
  };
  const last = gstatus?.last_sync ? new Date(gstatus.last_sync).toLocaleString() : "not yet";
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="google-sync-section">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-sky-400/15 text-sky-400"><RefreshCw className="h-4 w-4" /></div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">Two-way Google Calendar sync</p>
          <p className="text-xs text-muted-foreground">Events, task deadlines and trips flow into Google; your Google events show up here. Edits to BizForge events made in Google flow back.</p>
          {gstatus?.connected ? (
            <div className="mt-3 space-y-2">
              <p className="text-xs" data-testid="gcal-status-text"><span className="font-medium text-accent">Connected</span> as {gstatus.email} · last sync {last}{gstatus.needs_reauth ? " · needs reconnect" : ""}</p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={syncNow} disabled={!!busy} data-testid="gcal-sync-now">{busy === "sync" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />} Sync now</Button>
                {gstatus.needs_reauth && <Button size="sm" variant="outline" onClick={connect} disabled={!!busy} data-testid="gcal-reconnect">Reconnect</Button>}
                <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={disconnect} disabled={!!busy} data-testid="gcal-disconnect"><Unplug className="mr-1.5 h-3.5 w-3.5" /> Disconnect</Button>
              </div>
            </div>
          ) : gstatus?.configured ? (
            <Button size="sm" className="mt-3" onClick={connect} disabled={!!busy} data-testid="gcal-connect">{busy === "connect" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null} Connect Google Calendar</Button>
          ) : (
            <p className="mt-3 rounded-lg bg-secondary/50 px-3 py-2 text-xs text-muted-foreground" data-testid="gcal-not-configured">Google Calendar sync will be available once it's enabled for this workspace. Use the subscription link below in the meantime.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function SyncDialog({ open, onOpenChange, gstatus }) {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const buildSub = (token) => {
    const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
    const url = `${base}/api/calendar/feed/${token}.ics`;
    return { url, webcal: url.replace(/^https?:\/\//, "webcal://") };
  };

  React.useEffect(() => {
    if (open && !sub) {
      setLoading(true);
      api.get("/calendar/subscription").then((r) => setSub(buildSub(r.data.token))).catch(() => toast.error("Couldn't load your calendar link.")).finally(() => setLoading(false));
    }
  }, [open, sub]);

  const copy = async () => {
    if (!sub) return;
    await navigator.clipboard.writeText(sub.url);
    setCopied(true);
    toast.success("Subscription link copied");
    setTimeout(() => setCopied(false), 2000);
  };
  const download = () => { if (sub) window.open(sub.url, "_blank", "noopener"); };
  const rotate = async () => {
    if (!window.confirm("Generate a new link? Your old link will stop working everywhere it's added.")) return;
    setLoading(true);
    try { const r = await api.post("/calendar/subscription/rotate"); setSub(buildSub(r.data.token)); toast.success("New link generated"); }
    catch { toast.error("Couldn't refresh the link."); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="sync-calendar-dialog">
        <DialogHeader>
          <DialogTitle>Sync to your calendar</DialogTitle>
          <DialogDescription>Connect Google for two-way sync, or subscribe from any calendar app (one-way, read-only).</DialogDescription>
        </DialogHeader>
        <GoogleSyncSection gstatus={gstatus} />
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Subscribe from any calendar app</p>
        {loading && !sub ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>
        ) : sub ? (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Your private subscription link</Label>
              <div className="flex gap-2">
                <Input readOnly value={sub.url} className="font-mono text-xs" data-testid="sync-url-input" onFocus={(e) => e.target.select()} />
                <Button variant="outline" size="icon" onClick={copy} data-testid="copy-sync-url">{copied ? <Check className="h-4 w-4 text-accent" /> : <Copy className="h-4 w-4" />}</Button>
              </div>
              <p className="text-xs text-muted-foreground">Keep this link private — anyone with it can view your calendar.</p>
            </div>
            <div className="rounded-xl border border-border bg-secondary/40 p-3 text-xs leading-relaxed text-muted-foreground">
              <p className="mb-1.5 font-semibold text-foreground">How to add it</p>
              <p><span className="font-medium text-foreground">Google Calendar:</span> Other calendars → From URL → paste the link.</p>
              <p><span className="font-medium text-foreground">Apple Calendar:</span> File → New Calendar Subscription → paste the link.</p>
              <p><span className="font-medium text-foreground">Outlook:</span> Add calendar → Subscribe from web → paste the link.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={download} data-testid="download-ics-button"><Download className="mr-1.5 h-3.5 w-3.5" /> Download .ics (one-time import)</Button>
              <Button variant="ghost" size="sm" onClick={rotate} className="text-muted-foreground" data-testid="rotate-sync-button"><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Reset link</Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
